import { Box, Flex, Grid, Heading, Text } from "@radix-ui/themes";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import type { LiveSeoContent as LiveSeoPageData } from "~/data/liveSeoContent.functions";
import styles from "../live-seo-page.module.css";

type WelcomeMiamiOrthodonticsLocalization = {
	h1: string;
	date: string;
	heading: string;
	paragraph: string;
	relatedTitle: string;
	relatedWhyDate: string;
	relatedWhyTitle: string;
	relatedHalloweenDate: string;
	relatedHalloweenTitle: string;
};

const welcomeMiamiOrthodonticsLocalization = {
	[Locale.EN]: {
		h1: "Our Mission",
		date: "January 22, 2016",
		heading: "Welcome to Miami Orthodontic Specialists",
		paragraph:
			"Thank you for visiting our website to learn more about our practice. Our mission at Miami Orthodontic Specialists is to help you obtain the beautiful smile you’ve always dreamed of. We are dedicated to providing you and your family with an exceptional orthodontic experience and we strive to do this by combining state-of-the-art technology with friendly and patient-centered service. Dr. Garcia is a board-certified orthodontist who is dedicated to delivering the highest standard of care. She grew up in Miami, and—after almost a decade of training—is finally coming home to share her talents with her community. We look forward to welcoming you and your family to our office. Thank you for giving us the opportunity to transform the way you smile.",
		relatedTitle: "Also Worth Checking Out",
		relatedWhyDate: "Nov 23, 2016",
		relatedWhyTitle: "Why did you become an orthodontist?",
		relatedHalloweenDate: "Oct 31, 2016",
		relatedHalloweenTitle: "Happy Halloween!",
	},
	[Locale.ES]: {
		h1: "Nuestra misión",
		date: "22 de enero de 2016",
		heading: "Bienvenido a Miami Orthodontic Specialists",
		paragraph:
			"Gracias por visitar nuestro sitio web para conocer más sobre nuestra práctica. Nuestra misión en Miami Orthodontic Specialists es ayudarte a lograr la hermosa sonrisa con la que siempre has soñado. Estamos dedicados a brindarte a ti y a tu familia una experiencia ortodóncica excepcional, combinando tecnología de vanguardia con un servicio amable y centrado en el paciente. La Dra. Garcia es una ortodoncista certificada por la junta y dedicada a ofrecer el más alto estándar de atención. Creció en Miami y, después de casi una década de capacitación, finalmente regresa a casa para compartir sus talentos con su comunidad. Esperamos darte la bienvenida a ti y a tu familia en nuestra oficina. Gracias por darnos la oportunidad de transformar tu manera de sonreír.",
		relatedTitle: "También vale la pena leer",
		relatedWhyDate: "23 de noviembre de 2016",
		relatedWhyTitle: "¿Por qué te convertiste en ortodoncista?",
		relatedHalloweenDate: "31 de octubre de 2016",
		relatedHalloweenTitle: "¡Feliz Halloween!",
	},
} satisfies Record<Locale, WelcomeMiamiOrthodonticsLocalization>;

export function WelcomeMiamiOrthodonticsPage({
	page,
}: {
	page: LiveSeoPageData;
}) {
	const getLocalization = useLocalization(welcomeMiamiOrthodonticsLocalization);

	return (
		<Flex direction="column">
			<article className={styles.articleBand}>
				<Box className={styles.articleInner}>
					<OptimizedImage
						src="/same-day.jpg"
						alt="Young girl in orthodontic chair, smiling and giving a thumbs up, showcasing braces in Miami orthodontic office with dental equipment and x-ray monitor in background."
						width={page.image?.width}
						height={page.image?.height}
						className={styles.featuredImage}
						fetchPriority="high"
						sizes="(max-width: 767px) 92vw, 760px"
					/>
					<Heading as="h1" className={styles.postTitle}>
						{getLocalization("h1")}
					</Heading>
					<Text as="p" className={styles.postDate}>
						{getLocalization("date")}
					</Text>
					<Box className={styles.entryContent}>
						<Heading as="h3" className={styles.byline}>
							{getLocalization("heading")}
						</Heading>
						<Text as="p" size="4">
							{getLocalization("paragraph")}
						</Text>
					</Box>
					<section className={styles.relatedPosts}>
						<Heading as="h3" className={styles.relatedTitle}>
							{getLocalization("relatedTitle")}
						</Heading>
						<Grid columns={{ initial: "1", sm: "2" }} gap="5">
							<Link
								to="/why-i-became-an-orthodontist"
								className={styles.relatedCard}
							>
								<OptimizedImage
									src="/miami-ortho-13.jpg"
									alt="Smiling children with braces and an orthodontist in Miami, surrounded by greenery, promoting Miami Orthodontic Specialists' family-friendly care."
									width={2000}
									height={1335}
									className={styles.relatedImageWide}
									loading="lazy"
									sizes="(max-width: 767px) 65vw, 33vw"
								/>
								<Box className={styles.relatedContent}>
									<Text as="p" className={styles.postDate}>
										{getLocalization("relatedWhyDate")}
									</Text>
									<Heading as="h3" className={styles.relatedPostTitle}>
										{getLocalization("relatedWhyTitle")}
									</Heading>
								</Box>
							</Link>
							<Link to="/happy-halloween" className={styles.relatedCard}>
								<OptimizedImage
									src="/miami-ortho-4.jpg"
									alt="Children engaging with interactive tablets in a welcoming orthodontic office, showcasing Miami Orthodontic Specialists' family-friendly environment and patient-centered care."
									width={2000}
									height={1335}
									className={styles.relatedImageWide}
									loading="lazy"
									sizes="(max-width: 767px) 65vw, 33vw"
								/>
								<Box className={styles.relatedContent}>
									<Text as="p" className={styles.postDate}>
										{getLocalization("relatedHalloweenDate")}
									</Text>
									<Heading as="h3" className={styles.relatedPostTitle}>
										{getLocalization("relatedHalloweenTitle")}
									</Heading>
								</Box>
							</Link>
						</Grid>
					</section>
				</Box>
			</article>
		</Flex>
	);
}
